import {queryOptions, useQuery} from "@tanstack/react-query"
import {useEffect, useMemo} from "react"
import {useLocation, useSearchParams} from "react-router-dom"

import {
	extractOptOutToken,
	extractPaymentArrangementParams,
	extractPaymentPlanID,
	extractSecureCodeParams,
} from "./routeParamExtractors"

import {getCustomerInfo, type GetCustomerInfoParams} from "@indebted/api/customer/getCustomerInfo"

export const useCustomerInfoQuery = () => {
	const params = useCustomerInfoParams()
	return queryOptions({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: ["customerInfo"], // We don't want to re-fetch the customer info when the params change
		queryFn: () => {
			if (!params) {
				throw new Error("No params found")
			}
			return getCustomerInfo(params)
		},
		staleTime: 1000 * 60 * 15, // 15 minutes
		retryOnMount: false,
		enabled: !!params,
	})
}
const useCustomerInfo = () => {
	const query = useCustomerInfoQuery()

	const {data: customerInfo, isLoading, error, isError} = useQuery(query)

	useEffect(() => {
		if (error) {
			console.error("Failed to get customer info:", error)
		}
	}, [error])

	return {
		customerInfo,
		isLoading,
		error,
		isError,
	}
}

const useCustomerInfoParams = (): GetCustomerInfoParams | undefined => {
	const location = useLocation()
	const pathname = location.pathname
	const [queryParams] = useSearchParams()

	// Extract route parameters using the same logic as CustomerInfoTracker
	const secureCode = extractSecureCodeParams(pathname)
	const paymentPlanId = extractPaymentPlanID(pathname)
	const paymentArrangementId = extractPaymentArrangementParams(pathname)
	const token = extractOptOutToken(pathname)
	const customerId = queryParams.get("customerID") ?? queryParams.get("customer-id")

	const params = buildParams(secureCode, paymentPlanId, paymentArrangementId, customerId, token)

	useEffect(() => {
		if (!params) {
			console.error("No params found", pathname)
		}
	}, [params, pathname])

	return useMemo(() => {
		if (!params?.Type || !params?.Value) {
			return undefined
		}

		return {
			Type: params.Type,
			Value: params.Value,
		}
	}, [params?.Type, params?.Value])
}

const buildParams = (
	secureCode: string | undefined,
	paymentPlanId: string | undefined,
	paymentArrangementId: string | undefined,
	customerId: string | null,
	token: string | undefined,
): GetCustomerInfoParams | undefined => {
	if (paymentPlanId) {
		return {
			Type: "PaymentPlanID",
			Value: paymentPlanId,
		}
	}

	if (paymentArrangementId) {
		return {
			Type: "PaymentArrangementID",
			Value: paymentArrangementId,
		}
	}

	if (customerId) {
		return {
			Type: "CustomerID",
			Value: customerId,
		}
	}

	if (secureCode) {
		return {
			Type: "SecureCode",
			Value: secureCode,
		}
	}

	if (token) {
		return {
			Type: "Token",
			Value: token,
		}
	}

	return undefined
}

export {useCustomerInfo}
